import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { showConfirmDialog, showPurchaseDialog } from '../components/ConfirmDialog.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import type {
  MembershipStatusDto,
  MembershipPerksDto,
  ChestShopItemDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

function formatAUD(cents: number): string {
  return `A$${(cents / 100).toFixed(2)}`;
}

function formatExpiry(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium' });
}

export async function renderMembership(root: HTMLElement): Promise<void> {
  let current: MembershipStatusDto | null = null;
  let tiers: MembershipPerksDto[] = [];
  let chests: ChestShopItemDto[] = [];
  let flash: FlashState | null = null;

  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const r = await api.getMembership();
    current = r.current;
    tiers = r.tiers;
    chests = r.chests;
  } catch (e) {
    flash = { kind: 'danger', message: e instanceof HttpError ? e.message : 'Could not load Membership data.' };
  }

  // Purchase-confirmation copy. Close over the live lists so they read the
  // freshest names/crowns after a post-purchase re-read.
  const chestDetail = (slug: string | null | undefined): { detail: string; sub?: string | undefined } => {
    const c = slug ? chests.find((x) => x.slug === slug) : undefined;
    return c
      ? { detail: `Your ${c.name} has been dropped into thy inventory.`, sub: `Open it from your Inventory to claim ${formatCrowns(c.crowns)} crowns.` }
      : { detail: 'Your chest has been dropped into thy inventory.', sub: 'Find it waiting in your Inventory.' };
  };
  const charterDetail = (tierSlug: string | null | undefined): { detail: string; sub?: string | undefined } => {
    const t = tierSlug ? tiers.find((x) => x.slug === tierSlug) : undefined;
    const name = t?.name ?? 'new';
    const exp = current?.expiresAt ? formatExpiry(current.expiresAt) : null;
    return exp
      ? { detail: `The ${name} charter is now yours — larger bars and a daily refill for every resource.`, sub: `It runs until ${exp}.` }
      : { detail: `The ${name} charter is now yours — larger bars and a daily refill for every resource.` };
  };

  // Handle ?status=success|cancel on return from PayPal.
  // PayPal appends ?token=<orderId>&PayerID=... to the return URL, so the
  // approved order id arrives as `token`. We must explicitly capture it —
  // for intent=CAPTURE orders PayPal does NOT auto-capture on approval.
  const hashQuery = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const status = hashQuery.get('status');
  const paypalOrderId = hashQuery.get('token');
  if (status === 'success') {
    if (paypalOrderId) {
      try {
        const cap = await api.capturePayment(paypalOrderId);
        if (cap.fulfilled) {
          // Message keys off WHAT was bought (from the capture response), not
          // off whatever charter the account happens to already hold.
          flash = null;
          // Re-read so the page (and header bars) reflect the new entitlement.
          try {
            const me = await api.me();
            store.set({ account: me.account, character: me.character });
            const r = await api.getMembership();
            current = r.current;
            tiers = r.tiers;
            chests = r.chests;
          } catch { /* still confirm the purchase even if the re-read hiccups */ }
          // A modal the player must dismiss — never an easy-to-miss banner — so
          // there's no doubt the order went through.
          const info = cap.chestGranted ? chestDetail(cap.chestGranted) : charterDetail(cap.membership?.tier);
          void showPurchaseDialog({
            title: cap.chestGranted ? 'Chest claimed' : 'Charter sealed',
            detail: info.detail,
            sub: info.sub,
          });
        } else {
          flash = { kind: 'warn', message: `Payment received but not yet confirmed (status ${cap.status}). Check back in a moment.` };
        }
      } catch (e) {
        flash = {
          kind: 'danger',
          message: e instanceof HttpError ? e.message : 'Could not finalize the purchase. If you were charged it will reconcile shortly.',
        };
      }
    } else {
      flash = { kind: 'success', message: 'Thank you! Your purchase is being processed — check back in a moment if it has not yet appeared.' };
    }
    // Strip the query so a refresh doesn't re-run capture.
    history.replaceState(null, '', '#/membership');
  } else if (status === 'cancel') {
    flash = { kind: 'warn', message: 'Order cancelled — nothing has been charged.' };
    history.replaceState(null, '', '#/membership');
  }

  mountShell(root, (main) => {
    const renderMain = () => {
      const acc = store.get().account;
      const isActive = !!current?.active;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/membership.webp" alt="" onerror="this.remove()" />
        <h1>The Royal Charter</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Memberships of the realm — pen thy name in the great book.
        </p>

        <div id="flash"></div>

        <div class="panel">
          <h2 style="margin-top:0;">Current charter</h2>
          ${isActive
            ? `<p>You hold the <strong style="color:${tiers.find(t=>t.slug===current?.tier)?.flairColour ?? 'var(--c-gold-text)'}">${escapeHtml(tiers.find(t=>t.slug===current?.tier)?.name ?? current?.tier ?? '')}</strong> charter, expiring <strong>${formatExpiry(current?.expiresAt ?? null)}</strong>.</p>`
            : `<p style="color: var(--c-ink-soft); font-style: italic;">You hold no charter. Pick one below to gain bigger resource bars, daily refills for every resource, and a touch of flair beside thy name.</p>`}
        </div>

        <div class="panel">
          <h2 style="margin-top:0;">Charters</h2>
          <p style="font-style: italic; color: var(--c-ink-soft); margin-top:0;">
            All prices in Australian dollars (AUD); conversions shown are approximate. Each charter is a one-time purchase that lasts its full term, then
            simply ends — nothing renews automatically. Buy again anytime to extend. Annual plans save ~17%.
          </p>
          <div class="tier-grid">
            ${tiers.map((t) => renderTierCard(t, current, tiers)).join('')}
          </div>
        </div>

        <div class="panel">
          <h2 style="margin-top:0;">The Coin Shop</h2>
          <p style="font-style: italic; color: var(--c-ink-soft); margin-top:0;">
            Chests of crowns — sold to the Merchant for their full stamped value. Use them to
            jump-start a domicile purchase, fund a Scholarium tier, or seed a Bank vault. Buying
            does <em>not</em> affect your battle-stat climb to the Crucible.
          </p>
          <div class="chest-grid">
            ${chests.map(renderChestCard).join('')}
          </div>
        </div>

        <div class="panel">
          <h2 style="margin-top:0;">FAQ</h2>
          <dl class="kv">
            <dt>How is the membership delivered?</dt>
            <dd>The moment PayPal confirms your payment, the realm grants your charter automatically — bigger bars and daily refill tokens for every resource. No code to redeem.</dd>
            <dt>Does it renew automatically?</dt>
            <dd>No. A charter is a one-time purchase for its full term. When the term ends your bars revert to base — but every crown, item, and stat you earned is yours forever. To keep the perks, simply buy again; there's nothing to cancel.</dd>
            <dt>What happens if I upgrade to a higher charter early?</dt>
            <dd>You're never charged twice for the same days, and no time is lost. The unused value of your current charter is converted into time at the new tier, then the term you just bought is added on top. Because the higher tier costs more per day, that converted time is fewer days — but it's worth the same in coin, and it's now at the better tier. (Example: with ~30 days of Squire left, upgrading to Knight carries roughly 10 of those days plus your new month — about 40 days of Knight.) You cannot switch to a <em>lower</em> charter while a higher one is active; let it run its term instead.</dd>
            <dt>Does this affect the climb to the Crucible?</dt>
            <dd>Membership doubles your bars and grants daily refill tokens for every resource, which speeds your training. It does NOT scale battle-stat gains directly — the curve is preserved. Buying chests adds crowns, never battle stats.</dd>
            <dt>I bought a chest — where is it and how do I get the crowns?</dt>
            <dd>A chest is delivered straight to your <strong>Inventory</strong>, under the <em>Coin Chests</em> section — it does not add crowns on its own. To cash it in, open the Inventory, find the chest, and press <strong>Sell</strong>: the Merchant buys it for its <em>full stamped value</em> in crowns (chests sell at 100%, with no fee — unlike ordinary loot), and the coin lands in your wallet immediately. You're free to hold a chest as long as you like, or <strong>List</strong> it on the Merchant's Market to sell to another player instead.</dd>
            <dt>Refunds?</dt>
            <dd>All purchases — memberships and Coin Shop chests alike — are final and non-refundable. Please be sure before you confirm a purchase.</dd>
          </dl>
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = TIER_CSS;
      main.appendChild(style);

      // Flash
      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Wire purchase buttons
      main.querySelectorAll<HTMLButtonElement>('button[data-buy-tier]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const tierSlug = btn.dataset.buyTier!;
          const cycle = btn.dataset.cycle as 'monthly' | 'annual';
          await purchase({ productType: 'membership', productSlug: tierSlug, billingCycle: cycle });
        });
      });
      main.querySelectorAll<HTMLButtonElement>('button[data-buy-chest]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const chestSlug = btn.dataset.buyChest!;
          await purchase({ productType: 'chest', productSlug: chestSlug });
        });
      });
    };

    async function purchase(input: { productType: 'membership' | 'chest'; productSlug: string; billingCycle?: 'monthly' | 'annual' }) {
      try {
        const order = await api.createPaymentOrder(input);
        if (order.approvalUrl.startsWith('/payments/dev-approve')) {
          // Dev mode — auto-approve and fulfil.
          const okDev = await showConfirmDialog({
            title: 'Approve purchase?',
            message: `PayPal is not yet configured for this realm. Approve this purchase locally for ${formatAUD(order.amountCents)}?`,
            confirmLabel: 'Approve',
            tone: 'gold',
          });
          if (!okDev) return;
          const res = await api.devApprovePayment(order.paypalOrderId);
          if (res.fulfilled) {
            flash = null;
            // Refresh data
            const refreshed = await api.getMembership();
            current = refreshed.current;
            tiers = refreshed.tiers;
            chests = refreshed.chests;
            // refresh /me so the resource bar reflects new caps
            try { const me = await api.me(); store.set({ account: me.account, character: me.character }); } catch { /* ignore */ }
            anchored(renderMain);
            const info = input.productType === 'chest' ? chestDetail(input.productSlug) : charterDetail(input.productSlug);
            await showPurchaseDialog({
              title: input.productType === 'chest' ? 'Chest claimed' : 'Charter sealed',
              detail: info.detail,
              sub: info.sub,
            });
            return;
          }
          flash = { kind: 'warn', message: `Order recorded but not yet fulfilled (status ${res.status}).` };
          anchored(renderMain);
        } else {
          // Real PayPal — redirect.
          window.location.href = order.approvalUrl;
        }
      } catch (e) {
        flash = { kind: 'danger', message: e instanceof HttpError ? e.message : 'Order failed.' };
        anchored(renderMain);
      }
    }

    anchored(renderMain);
  });
}

function renderTierCard(t: MembershipPerksDto, current: MembershipStatusDto | null, tiers: MembershipPerksDto[]): string {
  const isCurrent = current?.active && current?.tier === t.slug;
  // Tiers are ordered ascending (squire < knight < baron < king). If an active charter
  // is held, disable any tier BELOW it — buying a lower charter would downgrade
  // (the server also rejects this with CANNOT_DOWNGRADE).
  const curRank = current?.active && current?.tier ? tiers.findIndex((x) => x.slug === current.tier) : -1;
  const thisRank = tiers.findIndex((x) => x.slug === t.slug);
  const isLower = curRank >= 0 && thisRank >= 0 && thisRank < curRank;
  const disabledAttr = isCurrent || isLower ? 'disabled' : '';
  const monthlyPrice = formatAUD(t.monthlyPriceCents);
  const annualPrice = formatAUD(t.annualPriceCents);
  // Render the tier name with the same black-outline + flair-colour glow
  // treatment used on the Profile player name, so prospective buyers see a
  // live preview of what their name will look like once charter is active.
  const flairNameStyle = `color: ${t.flairColour}; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 10px ${t.flairColour}88;`;
  return `
    <div class="tier-card" style="--flair: ${t.flairColour}">
      <div class="tier-card-head">
        <div class="tier-card-name" style="${flairNameStyle}">${escapeHtml(t.name)}</div>
        ${isCurrent ? `<span class="tier-current-badge">Active</span>` : ''}
      </div>
      <p class="tier-card-blurb">${escapeHtml(t.blurb)}</p>
      ${cgTable([cgCol('Includes', [
        cgRow('Max vigor / resolve / spirit', `${t.barMultiplier}×`, 'gain'),
        cgRow('Max life', `${t.lifeMultiplier}×`, 'gain'),
        cgRow('Daily refill tokens', `+${t.bonusTokensPerDay} to each resource`, 'gain'),
        cgRow('Name flair', 'Coloured', 'gain'),
      ])])}
      <div class="tier-prices">
        <button class="btn" data-buy-tier="${t.slug}" data-cycle="monthly" ${disabledAttr}>
          ${monthlyPrice} / month
        </button>
        <button class="btn ghost" data-buy-tier="${t.slug}" data-cycle="annual" ${disabledAttr}>
          ${annualPrice} / year
        </button>
      </div>
      ${isLower ? `<p class="tier-lower-note">Below thy active charter — ascend or renew instead.</p>` : ''}
    </div>
  `;
}

function renderChestCard(c: ChestShopItemDto): string {
  return `
    <div class="chest-card">
      <div class="chest-card-name">${escapeHtml(c.name)}</div>
      <div class="chest-card-coins">${formatCrowns(c.crowns)} crowns</div>
      <p class="chest-card-blurb">${escapeHtml(c.blurb)}</p>
      <button class="btn" data-buy-chest="${c.slug}">
        ${formatAUD(c.priceCents)}
      </button>
    </div>
  `;
}

const TIER_CSS = `
.tier-lower-note {
  margin: var(--s-2) 0 0; font-size: 0.8rem; font-style: italic;
  color: var(--c-ink-soft); text-align: center;
}
.tier-grid {
  display: grid; gap: var(--s-3);
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  margin-top: var(--s-3);
}
.tier-card {
  background: var(--c-parchment); background-image: var(--parchment-texture);
  border: 1px solid var(--c-rule-strong); border-radius: var(--radius);
  padding: var(--s-3);
  display: flex; flex-direction: column; gap: var(--s-2);
  border-top: 4px solid var(--flair);
  box-shadow: 0 2px 6px rgba(28, 17, 8, 0.15);
}
.tier-card-head { display: flex; justify-content: space-between; align-items: center; }
.tier-card-name {
  font-family: var(--font-heading); font-weight: 700; font-size: 1.4rem;
  letter-spacing: 0.06em; text-transform: uppercase;
}
.tier-current-badge {
  font-family: var(--font-display); font-size: 0.75rem; letter-spacing: 0.1em;
  text-transform: uppercase; color: #fff5e1; background: var(--c-oxblood);
  padding: 2px 8px; border-radius: 4px;
}
.tier-card-blurb { font-style: italic; color: var(--c-ink-soft); margin: 0; }
.tier-perks { list-style: none; padding: 0; margin: 0; }
.tier-perks li {
  padding: 4px 0;
  border-bottom: 1px dashed var(--c-rule-soft);
  font-size: 0.92rem;
}
.tier-perks li:last-child { border-bottom: 0; }
.tier-prices { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-2); }

.chest-grid {
  display: grid; gap: var(--s-3);
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-top: var(--s-3);
}
.chest-card {
  background: var(--c-parchment);
  border: 1px solid var(--c-rule); border-radius: var(--radius);
  padding: var(--s-3); display: flex; flex-direction: column; gap: var(--s-1);
}
.chest-card-name {
  font-family: var(--font-display); font-weight: 700; letter-spacing: 0.04em;
  color: var(--c-ink);
}
.chest-card-coins {
  font-family: var(--font-numeric); font-size: 1.2rem; color: var(--c-gold-text); font-weight: 700;
}
.chest-card-blurb {
  font-style: italic; color: var(--c-ink-soft); font-size: 0.85rem;
  flex: 1; margin: 0;
}
`;
